import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  VehicleTest,
  VehicleTestImages,
  is360ExteriorFrame,
} from "@/types/vehicleTest";
import { useMedia } from "@/hooks/useMedia";
import { Gavel, ShoppingCart, Play } from "lucide-react";
import FullscreenGallery from "@/components/car-listing/tire-info/FullscreenGallery";
import { Card } from "@/components/ui/card";
import { VehicleInformation } from "@/components/car-listing/VehicleInformation";
import AdditionalComments from "@/components/car-listing/AdditionalComments";
import TiresSection from "@/components/car-listing/TiresSection";
import Damages from "@/components/car-listing/Damages";
import ServiceHistory from "@/components/car-listing/ServiceHistory";
import TestProtocol from "@/components/car-listing/TestProtocol";
import { LicensePlate } from "@/components/my-auctions/LicensePlate";
import { ListingDetailsVehicleTest } from "@/types/listing";
import { useTheme } from "@/context/ThemeContext";
import { useTextColorClass } from "@/components/ui/text-coloring";
import { getUserTypeLabel } from "@/utils/userUtils";
import { carTestLabels } from "@/i18n/labels";
import { useLanguage } from "@/context/LanguageContext";
import { isVideoMedia } from "@/lib/isVideoMedia";
import { cn } from "@/lib/utils";

export type TestDocumentListingType = "auction" | "direct-purchase";

type MediaImageLike = {
  url?: string;
  mediaId?: string;
  previewMediaId?: string;
  fullSizeMediaId?: string;
};

interface VehicleTestDocumentViewProps {
  data: VehicleTest;
  /** När satt visas tydlig prisinfo: "Auktion" eller "Direktköp" */
  listingType?: TestDocumentListingType;
  /** För PDF-export: fast bredd, alla bilder med url, ingen lightbox */
  forPdf?: boolean;
}

function isEngineVideoImg(img: { type?: string; url?: string }): boolean {
  return isVideoMedia(img.type, img.url);
}

// Build flat list of all images with section and label for lightbox. Uses getUrl(img) so resolved mediaId→url is included.
// Engine/motor video is excluded from the exterior photo sequence and appended last.
function buildAllImages(
  data: VehicleTest,
  getUrl: (img: {
    url?: string;
    mediaId?: string;
    fullSizeMediaId?: string;
  }) => string,
  labels: ReturnType<typeof carTestLabels>,
): Array<{ url: string; alt: string; mediaType?: string }> {
  const images: Array<{ url: string; alt: string; mediaType?: string }> = [];
  const exteriorPhotos =
    data.images?.exterior?.filter(
      (img) => !is360ExteriorFrame(img) && !isEngineVideoImg(img),
    ) ?? [];
  exteriorPhotos.forEach((img, i) => {
    const u = getUrl(img);
    if (u)
      images.push({
        url: u,
        alt: `${labels.imageLabels?.exterior || "Exteriör"} ${i + 1}`,
        mediaType: img.type,
      });
  });
  data.images?.interior?.forEach((img, i) => {
    const u = getUrl(img);
    if (u)
      images.push({
        url: u,
        alt: `${labels.imageLabels?.interior || "Interiör"} ${i + 1}`,
        mediaType: img.type,
      });
  });
  data.images?.damage?.forEach((img, i) => {
    const u = getUrl(img);
    if (u)
      images.push({
        url: u,
        alt: `${labels.imageLabels?.damage || "Skada"} ${i + 1}`,
        mediaType: img.type,
      });
  });
  data.images?.serviceImages?.forEach((img, i) => {
    const u = getUrl(img);
    if (u)
      images.push({
        url: u,
        alt: `${labels.imageLabels?.service || "Service"} ${i + 1}`,
        mediaType: img.type,
      });
  });
  if (Array.isArray(data.images?.tires?.primary)) {
    data.images.tires.primary.forEach((tire, i) => {
      const u = getUrl(tire);
      if (u)
        images.push({
          url: u,
          alt: `${labels.imageLabels?.tirePrimary || "Primär däck"} ${i + 1}`,
          mediaType: tire.type,
        });
    });
  }
  if (Array.isArray(data.images?.tires?.secondary)) {
    data.images.tires.secondary.forEach((tire, i) => {
      const u = getUrl(tire);
      if (u)
        images.push({
          url: u,
          alt: `${labels.imageLabels?.tireSecondary || "Sekundär däck"} ${i + 1}`,
          mediaType: tire.type,
        });
    });
  }
  data.images?.exterior
    ?.filter((img) => !is360ExteriorFrame(img) && isEngineVideoImg(img))
    .forEach((img) => {
      const u = getUrl(img);
      if (u)
        images.push({
          url: u,
          alt:
            (labels.documentView as { engineVideo?: string })?.engineVideo ||
            "Motorvideo",
          mediaType: img.type,
        });
    });
  return images;
}

export const VehicleTestDocumentView: React.FC<
  VehicleTestDocumentViewProps
> = ({ data, listingType, forPdf = false }) => {
  const { language } = useLanguage();
  const ct = carTestLabels(language);
  const { getDownloadUrlAsync } = useMedia();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { baseText, subtleText } = useTextColorClass();

  const [openImage, setOpenImage] = useState<{
    url: string;
    alt: string;
    index: number;
  } | null>(null);
  const [previewUrls, setPreviewUrls] = useState<Map<string, string>>(
    new Map(),
  );
  const [resolvedMainUrls, setResolvedMainUrls] = useState<Map<string, string>>(
    new Map(),
  );
  const [resolvedFullSizeUrls, setResolvedFullSizeUrls] = useState<
    Map<string, string>
  >(new Map());

  const getDisplayUrl = React.useCallback(
    (img: { url?: string; mediaId?: string }) =>
      img?.url ||
      (img?.mediaId ? resolvedMainUrls.get(img.mediaId) : undefined) ||
      "",
    [resolvedMainUrls],
  );
  // Lightbox/popup must show FULL_SIZE; use resolvedFullSizeUrls when available
  const getLightboxUrl = React.useCallback(
    (img: { url?: string; mediaId?: string; fullSizeMediaId?: string }) =>
      (img?.fullSizeMediaId
        ? resolvedFullSizeUrls.get(img.fullSizeMediaId)
        : undefined) ||
      (img?.mediaId ? resolvedMainUrls.get(img.mediaId) : undefined) ||
      img?.url ||
      "",
    [resolvedFullSizeUrls, resolvedMainUrls],
  );
  const allImages = React.useMemo(
    () => buildAllImages(data, getLightboxUrl, ct),
    [data, getLightboxUrl, ct],
  );

  const resolvedImages = React.useMemo((): VehicleTestImages | undefined => {
    const withResolvedUrl = <T extends { url?: string; mediaId?: string }>(
      img: T,
    ): T =>
      img
        ? ({
            ...img,
            url: img.url || resolvedMainUrls.get(img.mediaId ?? "") || img.url,
          } as T)
        : img;
    if (!data.images) return data.images;
    return {
      ...data.images,
      damage: data.images.damage?.map((d) => withResolvedUrl(d)),
      serviceImages: data.images.serviceImages?.map((s) => withResolvedUrl(s)),
      tires: data.images.tires
        ? {
            primary: data.images.tires.primary?.map((t) => withResolvedUrl(t)),
            secondary: data.images.tires.secondary?.map((t) =>
              withResolvedUrl(t),
            ),
          }
        : undefined,
    };
  }, [data.images, resolvedMainUrls]);

  // Add an explicit `fullSizeUrl` for sections that use ImageGallery (Skador/Service/Hjul),
  // so fullscreen zoom matches Exteriör/Interiör quality (without forcing fullsize for thumbnails).
  const resolvedImagesWithFullSize = React.useMemo(() => {
    if (!resolvedImages) return resolvedImages;
    const withFullSizeUrl = <T extends MediaImageLike>(
      img: T,
    ): T & { fullSizeUrl?: string } =>
      img
        ? {
            ...img,
            fullSizeUrl:
              (img.fullSizeMediaId
                ? resolvedFullSizeUrls.get(img.fullSizeMediaId)
                : undefined) || img.url,
          }
        : img;

    return {
      ...resolvedImages,
      damage: resolvedImages.damage?.map((d) => withFullSizeUrl(d)),
      serviceImages: resolvedImages.serviceImages?.map((s) => withFullSizeUrl(s)),
      tires: resolvedImages.tires
        ? {
            primary: resolvedImages.tires.primary?.map((t) => withFullSizeUrl(t)),
            secondary: resolvedImages.tires.secondary?.map((t) =>
              withFullSizeUrl(t),
            ),
          }
        : undefined,
    };
  }, [resolvedImages, resolvedFullSizeUrls]);

  // Resolve main image URLs when API returns only mediaId (no url)
  useEffect(() => {
    if (forPdf) return;
    const list: Array<{ mediaId?: string; url?: string }> = [
      ...(data.images?.exterior?.filter((img) => !is360ExteriorFrame(img)) ||
        []),
      ...(data.images?.interior || []),
      ...(data.images?.damage || []),
      ...(data.images?.serviceImages || []),
      ...(data.images?.tires?.primary || []),
      ...(data.images?.tires?.secondary || []),
    ];
    const toFetch = list.filter((img) => img.mediaId && !img.url);
    if (toFetch.length === 0) {
      setResolvedMainUrls(new Map());
      return;
    }
    let cancelled = false;
    const urlMap = new Map<string, string>();
    Promise.all(
      toFetch.map(async (img) => {
        try {
          const url = await getDownloadUrlAsync({
            mediaId: img.mediaId!,
            expiresIn: 3600,
          });
          return { mediaId: img.mediaId!, url };
        } catch {
          return null;
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      results.forEach((r) => r && urlMap.set(r.mediaId, r.url));
      setResolvedMainUrls(urlMap);
    });
    return () => {
      cancelled = true;
    };
  }, [data.images, getDownloadUrlAsync, forPdf]);

  useEffect(() => {
    if (forPdf) return;
    const fetchPreviewUrls = async () => {
      const list: Array<MediaImageLike> = [
        ...(data.images?.exterior?.filter((img) => !is360ExteriorFrame(img)) ||
          []),
        ...(data.images?.interior || []),
        ...(data.images?.damage || []),
        ...(data.images?.serviceImages || []),
        ...(data.images?.tires?.primary || []),
        ...(data.images?.tires?.secondary || []),
      ];
      const previewPromises = list
        .filter(
          (img): img is MediaImageLike & { previewMediaId: string } =>
            !!img.previewMediaId && img.previewMediaId !== img.mediaId,
        )
        .map(async (img) => {
          try {
            const url = await getDownloadUrlAsync({
              mediaId: img.previewMediaId,
              expiresIn: 3600,
            });
            return { mediaId: img.previewMediaId, url };
          } catch {
            return null;
          }
        });
      const results = await Promise.all(previewPromises);
      const urlMap = new Map<string, string>();
      results.forEach((r) => r && urlMap.set(r.mediaId, r.url));
      setPreviewUrls(urlMap);
    };
    fetchPreviewUrls();
  }, [data.images, getDownloadUrlAsync]);

  // Resolve fullsize URLs for lightbox so popup always shows FULL_SIZE
  useEffect(() => {
    if (forPdf) return;
    const list: Array<MediaImageLike> = [
      ...(data.images?.exterior?.filter((img) => !is360ExteriorFrame(img)) ||
        []),
      ...(data.images?.interior || []),
      ...(data.images?.damage || []),
      ...(data.images?.serviceImages || []),
      ...(data.images?.tires?.primary || []),
      ...(data.images?.tires?.secondary || []),
    ];
    const toFetch = list.filter(
      (img): img is MediaImageLike & { fullSizeMediaId: string } =>
        !!img.fullSizeMediaId && img.fullSizeMediaId !== img.mediaId,
    );
    if (toFetch.length === 0) {
      setResolvedFullSizeUrls(new Map());
      return;
    }
    let cancelled = false;
    const urlMap = new Map<string, string>();
    Promise.all(
      toFetch.map(async (img) => {
        try {
          const url = await getDownloadUrlAsync({
            mediaId: img.fullSizeMediaId,
            expiresIn: 3600,
          });
          return { mediaId: img.fullSizeMediaId, url };
        } catch {
          return null;
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      results.forEach((r) => r && urlMap.set(r.mediaId, r.url));
      setResolvedFullSizeUrls(urlMap);
    });
    return () => {
      cancelled = true;
    };
  }, [data.images, getDownloadUrlAsync]);

  // Thumbnail/mini must always be PREVIEW; never fall back to fullsize (mainUrl) when we have previewMediaId
  const getThumbnailUrl = (image: MediaImageLike): string => {
    if (forPdf) return image.url || "";
    if (image.previewMediaId && image.previewMediaId !== image.mediaId) {
      return previewUrls.get(image.previewMediaId) || "";
    }
    return getDisplayUrl(image) || "";
  };

  const handleImageClick = async (imageUrl: string, alt: string) => {
    let index = allImages.findIndex((img) => img.url === imageUrl);
    if (index === -1) {
      index = allImages.findIndex(
        (img) =>
          img.url === imageUrl ||
          imageUrl.includes(img.url) ||
          img.url.includes(imageUrl),
      );
    }
    setOpenImage({
      url: imageUrl,
      alt,
      index: index >= 0 ? index : 0,
    });
  };

  const navigatePrev = () => {
    if (openImage && openImage.index > 0) {
      const prev = allImages[openImage.index - 1];
      setOpenImage({ ...prev, index: openImage.index - 1 });
    }
  };

  const navigateNext = () => {
    if (openImage && openImage.index < allImages.length - 1) {
      const next = allImages[openImage.index + 1];
      setOpenImage({ ...next, index: openImage.index + 1 });
    }
  };

  const closeImagePreview = useCallback(() => setOpenImage(null), []);
  const lightboxUrls = useMemo(
    () => allImages.map((img) => img.url),
    [allImages],
  );
  const lightboxMediaTypes = useMemo(
    () => allImages.map((img) => img.mediaType),
    [allImages],
  );
  const handleFullscreenSelect = useCallback(
    (index: number) => {
      const img = allImages[index];
      if (img) {
        setOpenImage({ url: img.url, alt: img.alt, index });
      }
    },
    [allImages],
  );

  const priceStr =
    typeof data.price === "string"
      ? data.price
      : (() => {
          const p =
            typeof data.price === "object" && data.price !== null
              ? (data.price as { desired?: string; estimated?: string })
              : null;
          return p?.desired ?? p?.estimated ?? ct.documentView.notSpecified;
        })();

  const priceLabel =
    listingType === "auction"
      ? ct.documentView.auction
      : listingType === "direct-purchase"
        ? ct.documentView.directPurchase
        : ct.documentView.desiredPrice;

  return (
    <div
      className={`space-y-4 md:space-y-6 py-2 animate-fade-in ${forPdf ? "w-full min-w-0" : ""}`}
      id="fordonsprotokoll-pdf"
      data-for-pdf={forPdf ? "true" : undefined}
    >
      {/* Licensskylt högst upp (titeln står i popup-rubriken – ingen dubbel, inte eget kort) */}
      {(data.vehicleDetails?.regNumber ?? data.regNumber) && (
        <div className="pb-1">
          <LicensePlate
            lpn={data.vehicleDetails?.regNumber ?? data.regNumber}
            className="shadow-sm"
          />
        </div>
      )}

      {/* 1. Säljarinfo */}
      <Card className="glass-card animate-fade-in overflow-hidden">
        <div className="p-4 md:p-6">
          <h2 className={`text-base md:text-xl font-semibold mb-4 ${baseText}`}>
            {ct.documentView.sellerInfo}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm md:text-sm">
            <div>
              <div className={`font-medium ${subtleText}`}>
                {ct.documentView.name}
              </div>
              <div className={baseText}>
                {data.customer?.username ?? ct.documentView.notSpecified}
              </div>
            </div>
            <div>
              <div className={`font-medium ${subtleText}`}>
                {ct.documentView.email}
              </div>
              <div className={baseText}>
                {data.customer?.email ?? ct.documentView.notSpecified}
              </div>
            </div>
            <div>
              <div className={`font-medium ${subtleText}`}>
                {ct.documentView.phone}
              </div>
              <div className={baseText}>
                {data.customer?.phoneNumber ?? ct.documentView.notSpecified}
              </div>
            </div>
            <div>
              <div className={`font-medium ${subtleText}`}>
                {ct.documentView.customerType}
              </div>
              <div className={baseText}>
                {data.customer?.role
                  ? getUserTypeLabel(data.customer.role)
                  : ct.documentView.notSpecified}
              </div>
            </div>
          </div>
        </div>

        {/* Pris – tydlig info direkt under säljarinfo (Auktion / Direktköp / Önskat pris) */}
        <div
          className={`border-t flex flex-wrap items-center gap-4 gap-y-3 px-4 md:px-6 py-4 ${
            isDark
              ? "border-white/15 bg-white/5"
              : "border-black/10 bg-black/[0.02]"
          }`}
        >
          <div className="flex items-center gap-2 min-w-0">
            {listingType === "auction" ? (
              <Gavel
                className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400"
                aria-hidden
              />
            ) : listingType === "direct-purchase" ? (
              <ShoppingCart
                className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400"
                aria-hidden
              />
            ) : null}
            <span
              className={`font-semibold text-sm md:text-sm uppercase tracking-wide ${subtleText}`}
            >
              {priceLabel}
            </span>
          </div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <span
              className={`text-xl md:text-2xl font-bold tabular-nums ${baseText}`}
            >
              {priceStr}
            </span>
            <span className={`text-sm ${subtleText}`}>
              {data.vatCar
                ? ct.documentView.vatIncluded
                : ct.documentView.vatExcluded}
            </span>
          </div>
        </div>
      </Card>

      {/* 2. Bilder – uppdelade per sektion, klickbara, navigerbara i lightbox */}
      <Card className="glass-card animate-fade-in">
        <div className="p-4 md:p-6">
          <h2 className={`text-base md:text-xl font-semibold mb-4 ${baseText}`}>
            {ct.documentView.images}
          </h2>
          <div className="space-y-6">
            {/* Exteriör – photos + motor video last in the same row */}
            {(() => {
              const exteriorAll =
                data.images?.exterior?.filter(
                  (img) => !is360ExteriorFrame(img),
                ) ?? [];
              const exteriorPhotos = exteriorAll.filter(
                (img) => !isEngineVideoImg(img),
              );
              const engineVideo =
                exteriorAll.find((img) => isEngineVideoImg(img)) ?? null;
              if (exteriorPhotos.length === 0 && !engineVideo) return null;

              const engineLabel =
                ct.documentView.engineVideo || "Motorvideo";
              const engineShort =
                ct.documentView.engineVideoShort || "Motor";
              const engineThumbSrc = engineVideo
                ? getThumbnailUrl(engineVideo) ||
                  getDisplayUrl(engineVideo) ||
                  ""
                : "";
              const engineVideoSrc =
                engineThumbSrc && !engineThumbSrc.includes("#")
                  ? `${engineThumbSrc}#t=0.001`
                  : engineThumbSrc;
              const thumbSize = forPdf ? "w-36 h-24" : "w-28 h-20";

              const openEngineVideo = async () => {
                if (!engineVideo) return;
                let url = getLightboxUrl(engineVideo);
                const fullId = engineVideo.fullSizeMediaId;
                if (fullId && fullId !== engineVideo.mediaId) {
                  try {
                    url = await getDownloadUrlAsync({
                      mediaId: fullId,
                      expiresIn: 3600,
                    });
                  } catch {
                    // keep url
                  }
                }
                if (url) handleImageClick(url, engineLabel);
              };

              return (
                <div>
                  <h3
                    className={`text-sm md:text-sm font-medium mb-1 ${subtleText}`}
                  >
                    {ct.documentView.exterior}
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {exteriorPhotos.map((image, index) => {
                      const displayUrl = getDisplayUrl(image);
                      if (!displayUrl) return null;
                      const isThumbnail = !!image.isThumbnail;
                      const label = isThumbnail
                        ? ct.documentView.thumbnail
                        : `${ct.documentView.exterior} ${index + 1}`;
                      return (
                        <button
                          key={`ex-${image.id || index}`}
                          type="button"
                          className="relative flex flex-col items-center text-left"
                          onClick={async () => {
                            let url = displayUrl;
                            const fullId = image.fullSizeMediaId;
                            if (fullId && fullId !== image.mediaId) {
                              try {
                                url = await getDownloadUrlAsync({
                                  mediaId: fullId,
                                  expiresIn: 3600,
                                });
                              } catch {
                                // keep url
                              }
                            }
                            handleImageClick(url, label);
                          }}
                        >
                          <img
                            src={getThumbnailUrl(image)}
                            alt={label}
                            className={`object-cover rounded-lg border shadow-sm ${!forPdf ? "hover:opacity-90 transition-opacity" : ""} ${thumbSize} ${isThumbnail ? "ring-2 ring-primary" : "border-border"}`}
                          />
                          {!isThumbnail && (
                            <span
                              className={`absolute top-1 left-1 flex h-5 min-w-5 items-center justify-center rounded-full text-xs font-semibold bg-black/70 text-white`}
                            >
                              {index + 1}
                            </span>
                          )}
                          {isThumbnail && (
                            <span className="text-xs mt-1.5 font-medium text-primary">
                              {label}
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {engineVideo ? (
                      forPdf ? (
                        <div className="relative flex flex-col items-center">
                          <div
                            className={cn(
                              "relative overflow-hidden rounded-lg border border-border shadow-sm",
                              thumbSize,
                            )}
                          >
                            <video
                              src={engineVideoSrc || getThumbnailUrl(engineVideo)}
                              muted
                              playsInline
                              preload="metadata"
                              className="absolute inset-0 h-full w-full object-cover"
                            />
                            <span className="absolute inset-0 bg-black/45" />
                            <span className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 text-white">
                              <Play
                                className="h-4 w-4 fill-white translate-x-px"
                                strokeWidth={0}
                              />
                              <span className="text-[9px] font-medium tracking-[0.14em] uppercase">
                                {engineShort}
                              </span>
                            </span>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={openEngineVideo}
                          className={cn(
                            "relative flex flex-col items-center text-left overflow-hidden rounded-lg border border-border shadow-sm hover:opacity-90 transition-opacity",
                            thumbSize,
                          )}
                          aria-label={engineLabel}
                          title={engineLabel}
                        >
                          <video
                            src={engineVideoSrc}
                            muted
                            playsInline
                            preload="metadata"
                            className="absolute inset-0 h-full w-full object-cover pointer-events-none"
                          />
                          <span className="absolute inset-0 bg-black/45" />
                          <span className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 text-white">
                            <Play
                              className="h-4 w-4 fill-white drop-shadow-md translate-x-px"
                              strokeWidth={0}
                            />
                            <span className="text-[9px] font-medium tracking-[0.14em] uppercase text-white/95 drop-shadow-sm">
                              {engineShort}
                            </span>
                          </span>
                        </button>
                      )
                    ) : null}
                  </div>
                </div>
              );
            })()}

            {/* Interiör – Bilder-kortet har endast Exteriör + Interiör (+ Huvudbild); övriga bilder i egna sektioner nedan */}
            {data.images?.interior?.length ? (
              <div className="mt-5">
                <h3
                  className={`text-sm md:text-sm font-medium mb-1 ${subtleText}`}
                >
                  {ct.documentView.interior}
                </h3>
                <div className="flex flex-wrap gap-3">
                  {data.images.interior.map((image, index) => {
                    const displayUrl = getDisplayUrl(image);
                    if (!displayUrl) return null;
                    const label = `${ct.documentView.interior} ${index + 1}`;
                    return (
                      <button
                        key={`in-${index}`}
                        type="button"
                        className="relative flex flex-col items-center text-left"
                        onClick={async () => {
                          let url = displayUrl;
                          const fullId = image.fullSizeMediaId;
                          if (fullId && fullId !== image.mediaId) {
                            try {
                              url = await getDownloadUrlAsync({
                                mediaId: fullId,
                                expiresIn: 3600,
                              });
                            } catch {
                              // keep url
                            }
                          }
                          handleImageClick(url, label);
                        }}
                      >
                        <img
                          src={getThumbnailUrl(image)}
                          alt={label}
                          className={`object-cover rounded-lg border border-border shadow-sm ${forPdf ? "w-36 h-24" : "w-28 h-20 hover:opacity-90 transition-opacity"}`}
                        />
                        <span
                          className={`absolute top-1 left-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-black/70 text-white text-xs font-semibold`}
                        >
                          {index + 1}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </Card>

      {/* 3. Fordonsinformation – hela utrustningen visas, ingen "Visa mer" */}
      <Card className="glass-card animate-fade-in overflow-hidden">
        <div className="p-4 md:p-6">
          <VehicleInformation
            vehicleDetails={data.vehicleDetails}
            vehicleTest={data}
            equipment={data.vehicleDetails?.equipment}
            showAdditionalInfo={true}
            showAllEquipment={true}
          />
        </div>
      </Card>

      {/* 4. Hjul – alltid utklappat i dokumentvy (ingen accordion) */}
      <TiresSection
        images={resolvedImagesWithFullSize?.tires}
        collapsible={false}
        singleColumnLayout
        onOpenImage={({ url, alt }) => handleImageClick(url, alt)}
        testData={{
          tires: data.vehicleTestData?.tires,
          brakes: data.vehicleTestData?.brakes,
          vehicleDetails: data.vehicleDetails,
        }}
      />

      {/* 5. Skador – alltid utklappat */}
      <Damages
        images={resolvedImagesWithFullSize?.damage}
        collapsible={false}
        onOpenImage={({ url, alt }) => handleImageClick(url, alt)}
      />

      {/* 6. Servicehistorik – alltid utklappat */}
      <ServiceHistory
        serviceImages={resolvedImagesWithFullSize?.serviceImages}
        serviceDetails={data.images?.serviceDetails?.serviceDetails}
        collapsible={false}
        singleColumnLayout
        onOpenImage={({ url, alt }) => handleImageClick(url, alt)}
      />

      {/* 7. Fordonstest – alltid utklappat, ingen accordion (PDF-lik scroll) */}
      <div className="rounded-lg backdrop-blur border bg-card text-card-foreground shadow-sm animate-fade-in">
        <div className="p-4 md:p-6">
          <h2 className={`text-lg md:text-xl font-semibold mb-4 ${baseText}`}>
            {ct.documentView.testProtocol}
          </h2>
          <TestProtocol
            vehicleTest={data as unknown as ListingDetailsVehicleTest}
            showComments={false}
            isCollapsible={false}
          />
        </div>
      </div>

      {/* 8. Övriga kommentarer – alltid utklappat */}
      <AdditionalComments comments={data.comments} collapsible={false} />

      {/* Lightbox – same fullscreen viewer as CarImageGallery (ej vid PDF) */}
      {!forPdf && (
        <FullscreenGallery
          images={lightboxUrls}
          mediaTypes={lightboxMediaTypes}
          title={ct.documentView.images}
          currentIndex={openImage?.index ?? 0}
          open={!!openImage}
          onOpenChange={(open) => {
            if (!open) closeImagePreview();
          }}
          onNavigate={(direction) => {
            if (direction === "prev") navigatePrev();
            else navigateNext();
          }}
          onSelect={handleFullscreenSelect}
        />
      )}
    </div>
  );
};
